import React from 'react'
import { StyleObject } from 'styletron-react'
import { lightTheme } from '@/customTheme'
import { hexToRgba } from '@/scenes/engine/utils/utils'
import { AI_GLOW_CIRCLE_STYLE } from '@/components/AiGlowCircle/AiGlowCircle'

/**
 * Shared visual tokens for GeneratedCard (formerly the inline styles duplicated
 * across PhotoshootCard and the Batch studio tile). Keeping them here means both
 * the card shell and its button presets read from one source.
 */

/** The cyan→magenta gradient drawn as the 4px border when a card is active. */
export const CARD_ACTIVE_GRADIENT = 'linear-gradient(135deg, #02fcff, #fc00ff)'

/** Light/dark checkerboard shown behind a transparent cutout (no color applied). */
export const CARD_CHECKER_BACKGROUND =
  'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 16px 16px'

/** Side of every card action button. */
export const CARD_ICON_BUTTON_SIZE = 40
/** Distance of a card's button cluster from the surface's bottom corner. */
export const CARD_BUTTON_INSET = 12

/** White 40×40 AI-glow circle used by every card action button. */
export const CARD_ICON_BUTTON_STYLE: StyleObject & React.CSSProperties = {
  ...AI_GLOW_CIRCLE_STYLE,
  width: `${CARD_ICON_BUTTON_SIZE}px`,
  height: `${CARD_ICON_BUTTON_SIZE}px`,
  minWidth: `${CARD_ICON_BUTTON_SIZE}px`,
  padding: 0,
  gap: 0,
  background: lightTheme.colors.white,
  outline: 'none',
  ':hover': { background: lightTheme.colors.white },
  ':focus-visible': {
    outline: `2px solid ${lightTheme.colors.primary}`,
    outlineOffset: '2px',
  },
}

/** White 40×40 circle with a plain black drop shadow (no AI glow) — the "Edit" hover button on a plain (non-AI) card. */
export const CARD_ICON_BUTTON_STYLE_PLAIN: StyleObject & React.CSSProperties = {
  ...CARD_ICON_BUTTON_STYLE,
  boxShadow: `0 0 20px 0 ${hexToRgba(lightTheme.colors.black, 0.15)}`,
}

/** Black 40×40 circle with the plain drop shadow; dims on hover. Pair it with a white glyph. */
export const CARD_ICON_BUTTON_STYLE_BLACK: StyleObject & React.CSSProperties = {
  ...CARD_ICON_BUTTON_STYLE_PLAIN,
  background: lightTheme.colors.blackGray,
  ':hover': { background: lightTheme.colors.blackGray, opacity: 0.88 },
  ':active': { background: lightTheme.colors.blackGray },
}

/** 1px inset hairline border drawn when a card is neither hovered nor selected. */
export const CARD_INSET_BORDER = `0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.1)} inset`

/** Outer frame whose background becomes the border when active. */
export function cardShellStyle(
  active: boolean,
  options?: { borderWidth?: number; borderColor?: string; borderRadius?: number },
): React.CSSProperties {
  const width = options?.borderWidth ?? 4
  const color = options?.borderColor
  const radius = options?.borderRadius ?? CARD_SHELL_OUTER_RADIUS
  return {
    padding: `${width}px`,
    borderRadius: `${radius}px`,
    background: active ? (color ?? CARD_ACTIVE_GRADIENT) : 'transparent',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
  }
}

const CARD_SHELL_OUTER_RADIUS = 20

/** Inner rounded surface that clips the preview + overlays. The caller spreads
 *  the resolved background (color / image / checker) on top of this. */
export function cardSurfaceStyle(borderWidth = 4, outerRadius = CARD_SHELL_OUTER_RADIUS): React.CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    borderRadius: `${Math.max(outerRadius - borderWidth, 0)}px`,
    overflow: 'hidden',
  }
}

/** Bottom action cluster anchored to a corner of the surface. */
export function cardButtonClusterStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    bottom: `${CARD_BUTTON_INSET}px`,
    [side]: `${CARD_BUTTON_INSET}px`,
    display: 'flex',
    gap: '12px',
  }
}

const BADGE_BASE: React.CSSProperties = {
  position: 'absolute',
  top: '12px',
  left: '12px',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

/** Top-left round check badge shown on a selected tile. */
export const CARD_SELECTED_BADGE_STYLE: React.CSSProperties = {
  ...BADGE_BASE,
  background: lightTheme.colors.primary,
}

/** Empty check circle shown (on hover) so an unselected tile has a clear select target. */
export const CARD_UNSELECTED_BADGE_STYLE: React.CSSProperties = {
  ...BADGE_BASE,
  background: hexToRgba(lightTheme.colors.white, 0.7),
  border: `1.5px solid ${lightTheme.colors.white}`,
  boxShadow: `0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.12)}`,
}

/** Bottom-anchored error message overlay. */
export const CARD_ERROR_OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-end',
  padding: '12px',
  background: hexToRgba(lightTheme.colors.black, 0.04),
  ...lightTheme.typography.Small12medium,
  color: lightTheme.colors.negative,
}

/** Caption rendered under the card. */
export const CARD_LABEL_STYLE: React.CSSProperties = {
  ...lightTheme.typography.Small14Semibold,
  margin: 0,
  textAlign: 'center',
  color: lightTheme.colors.blackGray,
}
